import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type BadgeTone =
  | 'neutral'
  | 'brand'
  | 'faculty'
  | 'reader'
  | 'lime'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'navy'

/**
 * Tones map to the ITPU palette. Each pairs an `-ink` foreground with a tint
 * ground, which is the combination the palette clears WCAG AA on — the pure
 * brand coral and bright blue do not, as small text.
 */
const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-tint text-text-body',
  brand: 'bg-brand-50 text-brand-700',
  faculty: 'bg-faculty-tint text-faculty-ink',
  reader: 'bg-reader-tint text-reader-ink',
  lime: 'bg-lime-tint text-lime-ink',
  success: 'bg-success-tint text-success',
  warning: 'bg-warning-tint text-warning',
  danger: 'bg-danger-tint text-danger',
  info: 'bg-info-tint text-info',
  navy: 'bg-itpu-navy text-itpu-white',
}

export function Badge({
  tone = 'neutral',
  icon,
  children,
  className,
  uppercase = false,
}: {
  tone?: BadgeTone
  icon?: ReactNode
  children: ReactNode
  className?: string
  uppercase?: boolean
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-sm px-2 py-[3px] text-caption font-semibold',
        uppercase && 'uppercase tracking-wide',
        TONES[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  )
}

/**
 * A discipline chip. `filled` marks a core text and the outline marks
 * supporting reading, which is the distinction the book page draws a legend
 * for (§12.7 item 5).
 */
export function Chip({
  filled = false,
  as: As = 'span',
  className,
  children,
  ...rest
}: {
  filled?: boolean
  as?: 'span' | 'button' | 'a'
  className?: string
  children: ReactNode
} & Record<string, unknown>) {
  return (
    <As
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-pill border px-3 py-[6px] text-meta transition-colors duration-fast',
        filled
          ? 'border-itpu-teal bg-itpu-teal text-on-brand'
          : 'border-border bg-transparent text-text-body hover:border-border-strong',
        As !== 'span' && 'cursor-pointer',
        className,
      )}
      {...rest}
    >
      {children}
    </As>
  )
}
